/* eslint-disable @typescript-eslint/no-shadow */
import { storyblokEditable } from '@storyblok/react';
import Image from 'next/image';
import { FC, ReactNode, useEffect } from 'react';

import Container from '@/components/elements/Container';
import Heading from '@/components/elements/Heading';
import PreHeading from '@/components/elements/PreHeading';
import Section from '@/components/elements/Section';
import { useStore } from '@/lib/store';
import { getStoryblokImageUrl } from '@/lib/utils';

export interface HeroBasicProps {
  children?: ReactNode;
  story: any;
  blok: any;
  pre_headline?: string;
  headline?: string;
  content?: string;
  actions: [];
  large_heading?: string;
  gapBottom?: string;
  image?: object;
  video?: object;
}

const HeroProject: FC<HeroBasicProps> = ({
  children,
  blok,
  pre_headline = blok.hero_pre_heading,
  headline = blok.hero_heading,
}) => {
  const setHeaderLight = useStore(({ setHeaderLight }) => setHeaderLight);
  useEffect(() => {
    setHeaderLight(true);
  }, []);

  if (blok.showHero === false) return <>{children}</>;

  return (
    <Section className={`relative`} {...storyblokEditable(blok)} gapBottom="lg">
      {children}
      <Container
        size="wide"
        className={`relative z-10 ${
          blok.header_theme === 'dark' ? 'dark bg-theme-black' : ''
        }`}
      >
        <div className="space-y-6 py-24">
          <div className="space-y-2">
            <PreHeading
              dot={true}
              size="lg"
              element="h2"
              color="primary"
              className="font-extralight"
            >
              {pre_headline}
            </PreHeading>
            <Heading size="lg" className="max-w-[20ch] font-semibold">
              {headline}
            </Heading>
          </div>
          <PreHeading
            size=""
            element="div"
            color="text-theme-darkgrey"
            className="mb-6 align-middle text-[14px] font-extralight tracking-widest text-theme-darkgrey"
          >
            {blok.tags?.map((t) => (
              <span
                key={t}
                className="mr-3 rounded-full bg-theme-lightgrey px-[16px] py-[4px] align-baseline"
              >
                {t}
              </span>
            ))}
          </PreHeading>
        </div>
      </Container>
      {blok.hero_feature_image?.filename && (
        <Container size="widest" gutter="none">
          <div className="relative">
            <Image
              alt={blok.hero_feature_image?.alt}
              src={getStoryblokImageUrl(
                blok.hero_feature_image?.filename,
                1400,
                0
              )}
              width={1200}
              height={0}
              priority
              className="w-full"
            />
          </div>
          <p className="!m-0 pt-2 font-mono text-xs text-neutral-500 empty:hidden lg:text-right">
            {blok.hero_feature_image?.title &&
              `${blok.hero_feature_image?.title}`}
            {blok.hero_feature_image?.copyright &&
              ` © ${blok.hero_feature_image?.copyright}`}
            {blok.hero_feature_image?.source &&
              ` / ${blok.hero_feature_image?.source}`}
          </p>
        </Container>
      )}
    </Section>
  );
};

export default HeroProject;
